import { computed, DOCUMENT, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthSessionService } from './auth-session.service';
import {
  buildPalette,
  DEFAULT_THEME,
  normalizeHex,
  splashColors,
  SplashColors,
  DEFAULT_SPLASH,
  THEME_TOKENS,
  ThemeMode,
  themeMode,
  ThemePalette,
} from '../utils/theme-palette.util';

/** A school's theme colour: its primary colour, or null for the default. */
type SchoolColor = string | null;

/** Areas of the app that belong to a school and so wear its colours. Admin and sign-in pages keep the defaults. */
const SCHOOL_AREAS = ['/principal', '/teacher'];

const CACHE_PREFIX = 'theme:';

const inSchoolArea = (url: string) => SCHOOL_AREAS.some((a) => url === a || url.startsWith(`${a}/`) || url.startsWith(`${a}?`));

/**
 * Applies a school's theme colours to the whole app by overriding the CSS
 * custom properties declared in src/styles/_variables.scss ($theme-defaults).
 *
 * SchoolContextService hands it the signed-in user's school colour; it's
 * applied only inside the principal and teacher areas, so admin pages and the
 * sign-in page (and an admin back from impersonating someone) keep the default
 * look. The last colour seen per school is cached in localStorage so a
 * reload starts in the school's colours instead of flashing the defaults; the
 * cache also carries the startup screen's colours (see src/index.html).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly auth = inject(AuthSessionService);
  private readonly router = inject(Router);

  /** The colour of the school it belongs to, keyed by that school. */
  private readonly school = signal<{ schoolId: number; primary: SchoolColor } | null>(this.cachedForCurrentUser());

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    // Before the first navigation ends, go by the address the app was opened on.
    { initialValue: this.doc.location?.pathname ?? '' },
  );

  /** The school colour in effect right now (null = the app's default). */
  private readonly active = computed<SchoolColor>(() => {
    const school = this.school();
    const userSchoolId = this.auth.user()?.schoolId ?? null;
    if (!school || school.schoolId !== userSchoolId || !inSchoolArea(this.url())) return null;
    return school.primary;
  });

  /** The palette in use right now. TS-side colours (charts, chips) read this. */
  readonly palette = computed<ThemePalette>(() => buildPalette(this.active()));

  /** 'dark' when the school's colour is light, so the app has dark backgrounds. */
  readonly mode = computed<ThemeMode>(() => themeMode(this.active()));

  constructor() {
    // Apply straight away (before the first render), then whenever it changes.
    this.write(this.palette(), this.mode());
    effect(() => this.write(this.palette(), this.mode()));
    // The startup screen's colours (src/index.html) also colour the page
    // behind the app (seen when a phone over-scrolls): keep them in step.
    effect(() => this.writeSplash(splashColors(this.active())));
  }

  /** Sets the colour of a school (from GET /schools/:id or after saving it). */
  setSchoolColor(schoolId: number, primary: SchoolColor): void {
    const clean = normalizeHex(primary);
    this.school.set({ schoolId, primary: clean });
    this.cache(schoolId, clean);
  }

  private write(palette: ThemePalette, mode: ThemeMode): void {
    const style = this.doc.documentElement?.style;
    if (!style) return;
    // Native controls (scrollbars, date pickers, select menus) follow along.
    if (mode === 'dark') style.setProperty('color-scheme', 'dark');
    else style.removeProperty('color-scheme');
    for (const token of THEME_TOKENS) {
      // Default values come from :root in styles.scss, so drop the override
      // rather than restating them.
      if (palette[token] === DEFAULT_THEME[token]) style.removeProperty(`--${token}`);
      else style.setProperty(`--${token}`, palette[token]);
    }
  }

  private writeSplash(splash: SplashColors): void {
    const style = this.doc.documentElement?.style;
    if (!style) return;
    for (const key of Object.keys(splash) as (keyof SplashColors)[]) {
      if (splash[key] === DEFAULT_SPLASH[key]) style.removeProperty(`--al-${key}`);
      else style.setProperty(`--al-${key}`, splash[key]);
    }
  }

  private cache(schoolId: number, primary: SchoolColor): void {
    try {
      if (!primary) localStorage.removeItem(CACHE_PREFIX + schoolId);
      // `splash` is read by the startup screen's inline script in src/index.html,
      // which runs before the app's code and so can't work the colours out itself.
      else localStorage.setItem(CACHE_PREFIX + schoolId, JSON.stringify({ primary, splash: splashColors(primary) }));
    } catch {
      // Storage unavailable (private mode, blocked): the theme still applies once the school loads.
    }
  }

  private cachedForCurrentUser(): { schoolId: number; primary: SchoolColor } | null {
    const schoolId = this.auth.user()?.schoolId;
    if (!schoolId) return null;
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + schoolId);
      if (!raw) return null;
      const primary = normalizeHex((JSON.parse(raw) as { primary?: string | null }).primary);
      return primary ? { schoolId, primary } : null;
    } catch {
      return null;
    }
  }

}
